import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const attempt = v.play();
    if (attempt && attempt.catch) attempt.catch(() => {});
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="grain-overlay relative flex min-h-screen items-end overflow-hidden">
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        poster="/videos/hero-poster.jpg"
        className="absolute inset-0 h-full w-full object-cover"
        aria-hidden="true"
        data-testid="hero-video"
      >
        <source src="/videos/hero-loop.mp4" type="video/mp4" />
        <source src="/videos/hero-loop.webm" type="video/webm" />
      </video>
      <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" aria-hidden="true" />
      <div className="absolute -left-24 top-1/3 h-96 w-96 rounded-full bg-electric/15 blur-3xl" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={0}
          data-testid="hero-eyebrow"
          className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-electric"
        >
          <span className="h-px w-12 bg-electric" aria-hidden="true" />
          Sovan Gourav Rout
        </motion.p>

        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          data-testid="hero-headline"
          className="font-display max-w-5xl text-5xl font-semibold uppercase leading-[0.98] tracking-tight text-neutral-100 sm:text-6xl lg:text-7xl"
        >
          Engineering Sound.
          <br />
          <span className="text-glow-amber text-electric">Creating Experiences.</span>
        </motion.h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          data-testid="hero-subheadline"
          className="mt-6 flex items-center gap-2 text-base font-semibold uppercase tracking-[0.15em] text-neutral-300 sm:text-lg"
        >
          <MapPin size={18} className="text-electric" aria-hidden="true" />
          Live Sound Engineer & Audio Specialist — Dubai & India
        </motion.p>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={3}
          className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg"
        >
          From live concerts and festivals to large-scale corporate productions, I engineer reliable audio
          systems and deliver precise sound experiences under pressure.
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#productions"
            data-testid="hero-view-work-button"
            className="rounded-full bg-electric px-8 py-4 text-xs font-extrabold uppercase tracking-[0.15em] text-[#050505] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#ffa733]"
          >
            View My Work
          </a>
          <a
            href="#contact"
            data-testid="hero-contact-button"
            className="rounded-full border border-white/25 bg-white/5 px-8 py-4 text-xs font-extrabold uppercase tracking-[0.15em] text-neutral-100 backdrop-blur-md transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1 hover:border-electric hover:bg-electric/10"
          >
            Let's Work Together
          </a>
        </motion.div>
      </div>
    </section>
  );
}
